import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withStyles, createStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Button } from '@material-ui/core';
import { createForm } from 'rc-form';

import { BROWSER_VARIABLE } from 'yeself-common/typings/application/ApplicationEnum';
import {
    APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT,
    APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT,
} from 'yeself-common/defaults/ApplicationDefaults';

import SwitchButton from 'yeself-client-common/form/components/SwitchButton';

import { updateApplicationThemeRequest } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationThemeVariantMainDefinition,
    selectApplicationFallbackVariantValuePath,
    selectApplicationFallbackVariantEnabled,
    selectApplicationThemeVariantEnabled,
    selectApplicationSelectedTheme,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';

import { selectAllowedThemesWithNotSetValue } from 'Console/data/applicationDetail/themes/selectors';
import { fetchThemesIds } from 'Console/data/applicationDetail/themes/actions';

import DropdownSelectForm from './DropdownSelectForm';

const styles = (theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            alignItems: 'center',
            padding: '0 24px',
            fontSize: theme.typography.pxToRem(14),
        },
        title: {
            display: 'inline-block',
            'font-weight': 'bold',
            'padding-bottom': theme.typography.pxToRem(20),
        },
        message: {
            fontSize: theme.typography.pxToRem(14),
        },
        btn: {
            marginBottom: theme.typography.pxToRem(65),
            marginTop: theme.typography.pxToRem(20),
        },
        items: {
            paddingTop: theme.typography.pxToRem(20),
            paddingBottom: theme.typography.pxToRem(20),
            [theme.breakpoints.down('lg')]: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
        },
        smallText: {
            fontSize: theme.typography.pxToRem(14),
            paddingRight: theme.spacing.unit * 3,
            paddingLeft: theme.spacing.unit * 3,
            [theme.breakpoints.down('lg')]: {
                paddingTop: theme.spacing.unit * 3,
                paddingBottom: theme.spacing.unit,
                paddingLeft: theme.typography.pxToRem(10),
                paddingRight: theme.typography.pxToRem(10),
            },
        },
        formInputLarge: {
            width: '65%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                marginBottom: '5%',
            },
        },
        formInput: {
            width: '65%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
            },
        },
        formName: {
            width: '30%',
            marginRight: '5%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                marginRight: 0,
                paddingBottom: theme.typography.pxToRem(10),
            },
        },
        textField: {
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                marginBottom: '5%',
            },
            marginTop: 0,
            marginBottom: 0,
            background: '#FAFAFA',
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)',
            borderRadius: 2,
        },
        wrapper: {
            width: '45%',
            display: 'flex',
            'align-items': 'center',
            'padding-bottom': 20,
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                paddingBottom: 'unset',
                flexWrap: 'wrap',
            },
        },
        fallbackWrapper: {
            width: '82.5%',
            display: 'flex',
            'align-items': 'center',
            'padding-bottom': 20,
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                paddingBottom: 'unset',
                flexWrap: 'wrap',
            },
        },
        fallbackTitle: {
            width: '19%',
            marginRight: '5%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                marginRight: 'unset',
            },
        },
        themeVariantWrapper: {
            'padding-bottom': 20,
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
                paddingBottom: 'unset',
                flexWrap: 'wrap',
            },
        },
        menu: {
            width: 250,
        },
        cssSelect: {
            '&:focus': {
                background: 'inherit',
            },
        },
        cssSelectRoot: {
            backgroundColor: 'inherit',
            borderRadius: 'inherit',
            '&:hover': {
                backgroundColor: 'inherit',
                borderBottom: 'none',
            },
            '&$focused': {
                backgroundColor: 'inherit',
            },
            '&$disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
            },
        },
        cssUnderline: {
            '&:before': {
                borderBottom: 'none',
            },
            '&:after': {
                borderBottom: 'none',
            },
            '&:hover:not([disabled]):not([focused]):not([error]):before': {
                borderBottom: `inherit !important`,
            },
        },
    });

const Theme = ({ classes, form, form: { getFieldValue, getFieldDecorator }, match }) => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const THEME_VARIANT_OPTIONS = [
        {
            id: BROWSER_VARIABLE.NOT_SET,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.NOT_SET}`,
                defaultMessage: "(Not set)"
            })
        },
        {
            id: BROWSER_VARIABLE.COOKIE,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.COOKIE}`,
                defaultMessage: "Cookie"
            })
        },
        {
            id: BROWSER_VARIABLE.LOCAL_STORAGE,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.LOCAL_STORAGE}`,
                defaultMessage: "Local Storage"
            })
        },
        {
            id: BROWSER_VARIABLE.JS_VARIABLE,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.JS_VARIABLE}`,
                defaultMessage: "JavaScript Variable"
            })
        },
        {
            id: BROWSER_VARIABLE.CSS_PATH,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.CSS_PATH}`,
                defaultMessage: "CSS Selector"
            })
        },
        {
            id: BROWSER_VARIABLE.URL_QUERY,
            name: formatMessage({
                id: `Settings.theme.options.${BROWSER_VARIABLE.URL_QUERY}`,
                defaultMessage: "URL Query"
            })
        },
    ];

    const variantDefinition = useSelector(selectApplicationThemeVariantMainDefinition);
    const { sourceType, valuePath } = variantDefinition;
    const fallbackVariantValuePath = useSelector(selectApplicationFallbackVariantValuePath);
    const fallbackVariantEnabled = useSelector(selectApplicationFallbackVariantEnabled);
    const themeVariantEnabled = useSelector(selectApplicationThemeVariantEnabled);
    const selectedThemeValue = useSelector(selectApplicationSelectedTheme);
    const allowedThemes = useSelector(selectAllowedThemesWithNotSetValue);

    useEffect(() => {
        if (match.params && match.params.appId) {
            dispatch(fetchThemesIds(match.params.appId));
        }
    }, [match, dispatch]);

    const handleSaveThemeSettings = useCallback(() => {
        const {
            enabled = false,
            main = APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT,
            fallback = APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT,
            selectedTheme,
        } = getFieldValue('theme');
        const { sourceType = BROWSER_VARIABLE.NOT_SET, path = '' } = main;
        const { fallbackEnabled = false, valuePath = '' } = fallback;

        dispatch(
            updateApplicationThemeRequest(match.params.appId, {
                themeVariant: {
                    enabled,
                    main: {
                        sourceType,
                        valuePath: path,
                    },
                    fallback: {
                        enabled: fallbackEnabled,
                        valuePath,
                    },
                },
                selectedTheme,
            })
        );
    }, [dispatch, getFieldValue, match.params.appId]);

    const isVariantPathDisabled = getFieldValue('theme.main.sourceType')
        ? getFieldValue('theme.main.sourceType') === BROWSER_VARIABLE.NOT_SET
        : sourceType === BROWSER_VARIABLE.NOT_SET;
    const isFallbackVariantPathDisabled = getFieldValue('theme.fallback.fallbackEnabled')
        ? getFieldValue('theme.fallback.fallbackEnabled') === false
        : fallbackVariantEnabled === false;

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.title}>
                    <FormattedMessage id="Settings.theme.titlePage" defaultMessage="Application theme" />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body1" className={classes.message} style={{ paddingBottom: 20 }}>
                    <FormattedMessage
                        id="Settings.theme.message"
                        defaultMessage="The space theme will be implemented for Adopt parts such as context help, journey tracker or progress tracker. For the guide, the theme can still be set separately in the Adopt editor."
                    />
                </Typography>
            </Grid>

            {getFieldDecorator('theme.selectedTheme', {
                initialValue: selectedThemeValue,
                preserve: true,
            })(
                <DropdownSelectForm
                    classNames={{
                        formInput: classes.formInput,
                        itemTextClassName: classes.formName,
                        textField: classes.textField,
                        menu: classes.menu,
                        cssSelectRoot: classes.cssSelectRoot,
                        cssUnderline: classes.cssUnderline,
                        wrapper: classes.wrapper,
                    }}
                    formattedMessage={{
                        id: 'Settings.theme.applicationTheme',
                        defaultMessage: 'Selected space theme',
                    }}
                    menuItems={{
                        options: allowedThemes,
                    }}
                />
            )}

            <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.title} style={{ paddingTop: 20 }}>
                    <FormattedMessage
                        id="Settings.theme.themeVariant.title"
                        defaultMessage="Theme variants"
                    />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body1" className={classes.message}>
                    <FormattedMessage
                        id="Settings.theme.themeVariant.message"
                        defaultMessage="The underlying system can dynamically change its look and feel be it between light or dark variant. Adopt should be aware of that change and react with a modified theme so the user experience is great. Therefore you need to instruct Adopt where to find the information about the type of the theme variant."
                    />
                </Typography>
            </Grid>
            <Grid container xs={12} direction="row" alignItems="flex-end" className={classes.items}>
                <Grid item className={classes.wrapper}>
                    <Typography variant="subtitle1" className={classes.formName}>
                        <FormattedMessage
                            id="Settings.theme.variantEnabled"
                            defaultMessage="Variants enabled"
                        />
                    </Typography>
                    <Grid item>
                        {getFieldDecorator('theme.enabled', {
                            initialValue: themeVariantEnabled,
                            preserve: true,
                        })(
                            <span id="yeself-switch-button">
                                <SwitchButton color="primary" value={getFieldValue('theme.enabled')}/>
                            </span>

                        )}
                    </Grid>
                </Grid>
            </Grid>

            {getFieldValue('theme.enabled') && (
                <Grid container>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        wrap="nowrap"
                        className={classes.themeVariantWrapper}
                    >
                        {getFieldDecorator('theme.main.sourceType', {
                            initialValue: sourceType || BROWSER_VARIABLE.NOT_SET,
                            preserve: true,
                        })(
                            <DropdownSelectForm
                                classNames={{
                                    formInput: classes.formInput,
                                    itemTextClassName: classes.formName,
                                    textField: classes.textField,
                                    menu: classes.menu,
                                    cssSelectRoot: classes.cssSelectRoot,
                                    cssUnderline: classes.cssUnderline,
                                    wrapper: classes.wrapper,
                                }}
                                formattedMessage={{
                                    id: 'Settings.theme.variantIdentifiedBy',
                                    defaultMessage: 'Variant identified by',
                                }}
                                menuItems={{
                                    options: THEME_VARIANT_OPTIONS,
                                }}
                            />
                        )}

                        <Grid className={classes.wrapper}>
                            <Grid item>
                                <Typography variant="body1" className={classes.smallText}>
                                    <FormattedMessage
                                        id="Settings.language.withPath"
                                        defaultMessage="with path"
                                    />
                                </Typography>
                            </Grid>

                            <Grid item className={classes.formInputLarge}>
                                <FormInput
                                    form={form}
                                    type="text"
                                    initialValue={valuePath}
                                    formItemKey="theme.main.path"
                                    placeholder={formatMessage({
                                        id: 'Settings.theme.withPath.placeholder',
                                    })}
                                    disabled={isVariantPathDisabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item className={classes.fallbackWrapper}>
                        <Typography variant="subtitle1" className={classes.fallbackTitle}>
                            <FormattedMessage
                                id="Settings.theme.fallbackVariantValue"
                                defaultMessage="Fallback variant value"
                            />
                        </Typography>
                        <Grid id="yeself-settigs-theme" item style={{ width: '10%' }}>
                            {getFieldDecorator('theme.fallback.fallbackEnabled', {
                                initialValue: fallbackVariantEnabled,
                                preserve: true,
                            })(
                                <span id="yeself-switch-button">
                                    <SwitchButton color="primary" value={getFieldValue('theme.fallback.fallbackEnabled')} />
                                </span>
                            )}
                        </Grid>

                        <FormInput
                            form={form}
                            type="text"
                            initialValue={fallbackVariantValuePath}
                            formItemKey="theme.fallback.valuePath"
                            placeholder={formatMessage({
                                id: 'Settings.theme.fallbackVariantValue.placeholder',
                            })}
                            disabled={isFallbackVariantPathDisabled}
                        />
                    </Grid>
                </Grid>
            )}

            <Grid item xs={12} alignItems="center">
                <Typography variant="button">
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveThemeSettings}
                        className={classes.btn}
                    >
                        <FormattedMessage id="Buttons.save" />
                    </Button>
                </Typography>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(createForm()(Theme));
